import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiCall, uploadFile } from '../api/client';
import AudioButton from '../components/AudioButton';
import LessonLevelEditor from '../components/LessonLevelEditor';
import LessonPlannerChat from '../components/LessonPlannerChat';
import CollapsibleEditorCard from '../components/CollapsibleEditorCard';
import { LESSON_TYPES } from '../utils/lessons';

const levelType = (l) => l.type || 'drill';
const isLessonLevel = (l) => LESSON_TYPES.includes(levelType(l));

function AudioIndicator({ src }) {
  return (
    <span
      title="Play audio"
      className="inline-flex align-middle mx-1"
      // Previewing a suggestion must keep the input focused and the list open.
      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
      onClick={e => e.stopPropagation()}
    >
      <AudioButton src={src} size="xs" />
    </span>
  );
}

// Levels in pedagogical order: by `order`, array position as the tiebreaker.
function orderLevels(levels) {
  return (levels || [])
    .map((level, index) => ({ level, index }))
    .sort((a, b) => (a.level.order ?? 0) - (b.level.order ?? 0) || a.index - b.index)
    .map(x => x.level);
}

// A table cell for an audio column value: a play button (when set) plus an upload
// control that picks a file and hands it to `onUpload`. Manages its own busy state.
function AudioCell({ value, onUpload }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(file) {
    setBusy(true);
    try {
      await onUpload(file);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {value
        ? <AudioButton src={value} size="sm" />
        : <span className="text-xs text-amber-400 whitespace-nowrap">No audio</span>}
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleFile(f); }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="text-xs font-medium text-amber-600 hover:text-amber-800 disabled:text-amber-300 underline whitespace-nowrap"
      >
        {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
      </button>
    </div>
  );
}

function LevelEditor({ level, courseId, db, columns, columnTypes, token, onEntriesChange, controls, collapsed, onToggle }) {
  const [newSource, setNewSource] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [newAudio, setNewAudio] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [adding, setAdding] = useState(false);
  const [wordError, setWordError] = useState('');

  const sourceIsAudio = columnTypes[level.sourceColumn] === 'audio';
  const targetIsAudio = columnTypes[level.targetColumn] === 'audio';
  const srcLabel = columns[level.sourceColumn] ?? `Column ${level.sourceColumn}`;
  const tgtLabel = columns[level.targetColumn] ?? `Column ${level.targetColumn}`;

  // The course's audio column when it's neither this level's source nor target:
  // its clip is stored on the entry (entry.audio) so it can play during study even
  // though it isn't the prompt or answer. -1 / falsy when there's no such column.
  const audioCol = columnTypes.findIndex(t => t === 'audio');
  const separateAudio = audioCol !== -1 && audioCol !== level.sourceColumn && audioCol !== level.targetColumn;

  // Upload an audio clip and store its URL on an existing entry's source/target side.
  async function handleReplaceAudio(entry, side, file) {
    setWordError('');
    try {
      const { audioUrl } = await uploadFile(`/courses/${courseId}/audio`, file, 'audio', token);
      const updated = await apiCall(
        'PATCH', `/courses/${courseId}/levels/${level.id}/entries/${entry.id}`,
        { [side]: audioUrl }, token
      );
      onEntriesChange(level.id, level.entries.map(e => (e.id === entry.id ? updated : e)));
    } catch (err) {
      setWordError(err.message || 'Failed to upload audio.');
    }
  }

  // Upload an audio clip for the new-word row and stash its URL in the right field.
  async function handleNewAudioUpload(side, file) {
    setWordError('');
    try {
      const { audioUrl } = await uploadFile(`/courses/${courseId}/audio`, file, 'audio', token);
      if (side === 'source') setNewSource(audioUrl); else setNewTarget(audioUrl);
    } catch (err) {
      setWordError(err.message || 'Failed to upload audio.');
    }
  }

  function computeSuggestions(field, value) {
    if (!db || !value.trim()) return [];
    const colIdx = field === 'source' ? level.sourceColumn : level.targetColumn;
    const query = value.trim().toLowerCase();
    const normalizedValue = e => (e.values[colIdx] ?? '').trim().toLowerCase();
    return db.entries
      .filter(e => normalizedValue(e).startsWith(query))
      // Rank before limiting so short exact matches cannot be crowded out.
      .sort((a, b) => Number(normalizedValue(b) === query) - Number(normalizedValue(a) === query))
      .slice(0, 6);
  }

  function handleNewWordChange(field, value) {
    if (field === 'source') setNewSource(value);
    else setNewTarget(value);
    // Typing invalidates any audio captured from a previously picked suggestion,
    // so it can't be saved against a word the user has since edited.
    setNewAudio('');
    setSuggestions(computeSuggestions(field, value));
    setActiveField(field);
  }

  function applySuggestion(entry) {
    setNewSource(entry.values[level.sourceColumn] ?? '');
    setNewTarget(entry.values[level.targetColumn] ?? '');
    // Capture the picked database row's audio clip so it's stored on the new entry.
    setNewAudio(separateAudio ? (entry.values[audioCol] ?? '') : '');
    setSuggestions([]);
    setActiveField(null);
  }

  function dismissSuggestions() {
    setTimeout(() => { setSuggestions([]); setActiveField(null); }, 150);
  }

  async function handleAddWord() {
    if (!newSource.trim() || !newTarget.trim()) return;
    setAdding(true);
    setWordError('');
    try {
      const entry = await apiCall(
        'POST', `/courses/${courseId}/levels/${level.id}/entries`,
        { source: newSource.trim(), target: newTarget.trim(), ...(newAudio ? { audio: newAudio } : {}) },
        token
      );
      onEntriesChange(level.id, [...level.entries, entry]);
      setNewSource('');
      setNewTarget('');
      setNewAudio('');
    } catch (err) {
      setWordError(err.message || 'Failed to add word.');
    } finally {
      setAdding(false);
    }
  }

  async function handleDeleteEntry(entryId) {
    if (!window.confirm('Delete this word? It will be removed from this level and from every learner\'s progress. This cannot be undone.')) return;
    setWordError('');
    try {
      await apiCall(
        'DELETE', `/courses/${courseId}/levels/${level.id}/entries/${entryId}`,
        null, token
      );
      onEntriesChange(level.id, level.entries.filter(e => e.id !== entryId));
    } catch (err) {
      setWordError(err.message || 'Failed to delete word.');
    }
  }

  async function handleEditEntry(entryId, field, value, entry) {
    setWordError('');
    try {
      await apiCall(
        'PATCH', `/courses/${courseId}/levels/${level.id}/entries/${entryId}`,
        { source: field === 'source' ? value : entry.source, target: field === 'target' ? value : entry.target },
        token
      );
    } catch (err) {
      setWordError(err.message || 'Failed to save word.');
    }
  }

  return (
    <CollapsibleEditorCard
      title={level.title}
      subtitle={`${level.entries.length} ${level.entries.length === 1 ? 'word' : 'words'}`}
      controls={controls}
      collapsed={collapsed}
      onToggle={onToggle}
    >

      <div className="rounded-lg border border-amber-100 overflow-visible">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-amber-50 border-b border-amber-100">
              <th className="text-left px-3 py-2 text-amber-700 font-medium">{srcLabel}{sourceIsAudio && ' 🔊'}</th>
              <th className="text-left px-3 py-2 text-amber-700 font-medium">{tgtLabel}{targetIsAudio && ' 🔊'}</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {level.entries.map((entry, i) => (
              <tr key={entry.id} className={i % 2 === 0 ? '' : 'bg-amber-50/40'}>
                <td className="px-3 py-1.5">
                  {sourceIsAudio ? (
                    <AudioCell value={entry.source} onUpload={file => handleReplaceAudio(entry, 'source', file)} />
                  ) : (
                    <input
                      defaultValue={entry.source}
                      onBlur={e => handleEditEntry(entry.id, 'source', e.target.value, entry)}
                      className="w-full bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 rounded px-1 py-0.5 text-amber-900"
                    />
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {targetIsAudio ? (
                    <AudioCell value={entry.target} onUpload={file => handleReplaceAudio(entry, 'target', file)} />
                  ) : (
                    <input
                      defaultValue={entry.target}
                      onBlur={e => handleEditEntry(entry.id, 'target', e.target.value, entry)}
                      className="w-full bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 rounded px-1 py-0.5 text-amber-900"
                    />
                  )}
                </td>
                <td className="px-2 py-1.5 text-center whitespace-nowrap">
                  {entry.audio && <AudioIndicator src={entry.audio} />}
                  <button
                    type="button"
                    onClick={() => handleDeleteEntry(entry.id)}
                    title="Delete word"
                    aria-label="Delete word"
                    className="text-amber-300 hover:text-red-500 transition text-lg leading-none"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}

            {/* New word row */}
            <tr className="border-t border-dashed border-amber-200">
              <td className="px-2 py-2 relative">
                {sourceIsAudio ? (
                  <AudioCell value={newSource} onUpload={file => handleNewAudioUpload('source', file)} />
                ) : (
                  <>
                    <input
                      value={newSource}
                      onChange={e => handleNewWordChange('source', e.target.value)}
                      onBlur={dismissSuggestions}
                      placeholder={`New ${srcLabel.toLowerCase()}…`}
                      className="w-full border border-amber-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                    />
                    {activeField === 'source' && suggestions.length > 0 && (
                      <ul className="absolute left-0 right-0 z-30 bg-white border border-amber-200 rounded-lg shadow-lg mt-0.5">
                        {suggestions.map(entry => (
                          <li
                            key={entry.id}
                            onMouseDown={() => applySuggestion(entry)}
                            className="px-3 py-2 hover:bg-amber-50 cursor-pointer"
                          >
                            {entry.values[audioCol] && <AudioIndicator src={entry.values[audioCol]} />}
                            <span className="font-medium text-amber-900">{sourceIsAudio ? '🔊' : entry.values[level.sourceColumn]}</span>
                            <span className="text-amber-300 mx-1.5">→</span>
                            <span className="text-amber-600">{targetIsAudio ? '🔊' : entry.values[level.targetColumn]}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </td>
              <td className="px-2 py-2 relative">
                {targetIsAudio ? (
                  <AudioCell value={newTarget} onUpload={file => handleNewAudioUpload('target', file)} />
                ) : (
                  <>
                    <input
                      value={newTarget}
                      onChange={e => handleNewWordChange('target', e.target.value)}
                      onBlur={dismissSuggestions}
                      placeholder={`New ${tgtLabel.toLowerCase()}…`}
                      className="w-full border border-amber-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                    />
                    {activeField === 'target' && suggestions.length > 0 && (
                      <ul className="absolute left-0 right-0 z-30 bg-white border border-amber-200 rounded-lg shadow-lg mt-0.5">
                        {suggestions.map(entry => (
                          <li
                            key={entry.id}
                            onMouseDown={() => applySuggestion(entry)}
                            className="px-3 py-2 hover:bg-amber-50 cursor-pointer"
                          >
                            {entry.values[audioCol] && <AudioIndicator src={entry.values[audioCol]} />}
                            <span className="text-amber-600">{sourceIsAudio ? '🔊' : entry.values[level.sourceColumn]}</span>
                            <span className="text-amber-300 mx-1.5">→</span>
                            <span className="font-medium text-amber-900">{targetIsAudio ? '🔊' : entry.values[level.targetColumn]}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </td>
              <td className="px-2 py-2 text-center">{newAudio && <AudioIndicator src={newAudio} />}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {wordError && <p className="text-red-500 text-sm mt-3">{wordError}</p>}

      <button
        onClick={handleAddWord}
        disabled={adding || !newSource.trim() || !newTarget.trim()}
        className="mt-3 text-sm font-medium text-amber-600 hover:text-amber-800 disabled:text-amber-300 transition"
      >
        + Add word
      </button>
    </CollapsibleEditorCard>
  );
}

export default function EditCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token, userId } = useAuth();
  const onBack = () => navigate(`/courses/${courseId}`);
  const onDeleted = () => navigate('/', { replace: true });
  const [course, setCourse] = useState(null);
  const [collapsedLevels, setCollapsedLevels] = useState({});
  const [db, setDb] = useState(null);
  const [dbMissing, setDbMissing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  const [newLevelTitle, setNewLevelTitle] = useState('');
  const [newLevelSource, setNewLevelSource] = useState(0);
  const [newLevelTarget, setNewLevelTarget] = useState(1);
  const [addingLevel, setAddingLevel] = useState(false);
  const [levelError, setLevelError] = useState('');

  const [newLessonType, setNewLessonType] = useState('grammar');
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [addingLesson, setAddingLesson] = useState(false);
  const [lessonError, setLessonError] = useState('');

  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnType, setNewColumnType] = useState('text');
  const [columnError, setColumnError] = useState('');
  const [columnBusy, setColumnBusy] = useState(false);

  const [deleting, setDeleting] = useState(false);

  const fileInputRef = useRef(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconError, setIconError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const courseData = await apiCall('GET', `/courses/${courseId}`, null, token);
        // The editor is owner-only (the server enforces this on every write);
        // a deep link by anyone else lands on the course page instead.
        if (courseData.ownerId !== userId) {
          navigate(`/courses/${courseId}`, { replace: true });
          return;
        }
        setCourse(courseData);
        setCollapsedLevels(Object.fromEntries(courseData.levels.map(level => [level.id, true])));
        setTitle(courseData.title);
        setDescription(courseData.description || '');
        if (courseData.databaseId) {
          // A missing database (e.g. deleted since the course was created) must not
          // block the editor — the owner still needs it to fix or delete the course.
          try {
            const dbData = await apiCall('GET', `/databases/${courseData.databaseId}`, null, token);
            setDb(dbData);
            setNewLevelTarget(dbData.columns.length > 1 ? 1 : 0);
          } catch (err) {
            console.error(err);
            setDbMissing(true);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [courseId, token]);

  function handleEntriesChange(levelId, entries) {
    setCourse(prev => ({
      ...prev,
      levels: prev.levels.map(l => l.id === levelId ? { ...l, entries } : l)
    }));
  }

  async function handleSaveSettings() {
    if (!title.trim()) return;
    setSavingSettings(true);
    setSettingsError('');
    try {
      await apiCall('PATCH', `/courses/${courseId}`, { title: title.trim(), description: description.trim() }, token);
      setCourse(prev => ({ ...prev, title: title.trim(), description: description.trim() }));
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch (err) {
      setSettingsError(err.message || 'Failed to save changes.');
    } finally {
      setSavingSettings(false);
    }
  }

  async function handleIconChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setIconError('');
    setUploadingIcon(true);
    try {
      const { iconUrl } = await uploadFile(`/courses/${courseId}/icon`, file, 'icon', token);
      setCourse(prev => ({ ...prev, iconUrl }));
    } catch (err) {
      setIconError(err.message || 'Failed to upload image.');
    } finally {
      setUploadingIcon(false);
    }
  }

  async function handleTogglePublish() {
    try {
      const result = await apiCall('PATCH', `/courses/${courseId}/publish`, null, token);
      setCourse(prev => ({ ...prev, isPublished: result.isPublished }));
    } catch (err) {
      console.error(err);
    }
  }

  async function handleAddLevel() {
    if (!newLevelTitle.trim()) return;
    setLevelError('');
    const srcCol = Number(newLevelSource);
    const tgtCol = Number(newLevelTarget);
    if (srcCol === tgtCol) {
      setLevelError('Question and answer columns must be different.');
      return;
    }
    setAddingLevel(true);
    try {
      const level = await apiCall(
        'POST', `/courses/${courseId}/levels`,
        { title: newLevelTitle.trim(), sourceColumn: srcCol, targetColumn: tgtCol },
        token
      );
      setCourse(prev => ({ ...prev, levels: [...prev.levels, { ...level, entries: level.entries ?? [] }] }));
      setNewLevelTitle('');
    } catch (err) {
      setLevelError(err.message || 'Failed to add level.');
    } finally {
      setAddingLevel(false);
    }
  }

  // Create a lesson level. It needs a minimally-valid shape to pass server validation
  // (a non-empty objectives array is required), so it's seeded with a placeholder the
  // author then fills in via the lesson editor.
  async function handleAddLesson() {
    const t = newLessonTitle.trim();
    if (!t) return;
    setAddingLesson(true);
    setLessonError('');
    try {
      const level = await apiCall(
        'POST', `/courses/${courseId}/levels`,
        {
          type: newLessonType, title: t,
          ...(newLessonType === 'grammar-drill' ? { grammarPoint: t } : {}),
          // Default to the first two columns as native (source) / taught (target); the
          // author can reassign them in the lesson editor.
          sourceColumn: 0, targetColumn: availableColumns.length > 1 ? 1 : 0,
          ...(newLessonType !== 'grammar-drill' ? { objectives: ['Objective (edit me)'] } : {}),
        },
        token
      );
      setCourse(prev => ({ ...prev, levels: [...prev.levels, level] }));
      setNewLessonTitle('');
    } catch (err) {
      setLessonError(err.message || 'Failed to add lesson.');
    } finally {
      setAddingLesson(false);
    }
  }

  function handleLevelSaved(updated) {
    setCourse(prev => ({ ...prev, levels: prev.levels.map(l => (l.id === updated.id ? { ...l, ...updated } : l)) }));
  }

  async function handleDeleteLevel(levelId, levelTitle) {
    if (!window.confirm(`Delete "${levelTitle || 'this level'}"? This removes it and every learner's progress for it. This cannot be undone.`)) return;
    try {
      await apiCall('DELETE', `/courses/${courseId}/levels/${levelId}`, null, token);
      setCourse(prev => ({ ...prev, levels: prev.levels.filter(l => l.id !== levelId) }));
    } catch (err) {
      console.error(err);
      window.alert(err.message || 'Failed to delete level.');
    }
  }

  // Move a level up/down in the curriculum by swapping order with its neighbour, then
  // renumbering so orders stay sequential. Optimistic; resyncs from the server on error.
  async function handleMoveLevel(levelId, dir) {
    const sorted = orderLevels(course.levels);
    const i = sorted.findIndex(l => l.id === levelId);
    const j = i + dir;
    if (j < 0 || j >= sorted.length) return;
    [sorted[i], sorted[j]] = [sorted[j], sorted[i]];

    const updates = sorted
      .map((l, idx) => ({ id: l.id, order: idx + 1 }))
      .filter(u => course.levels.find(l => l.id === u.id)?.order !== u.order);
    if (updates.length === 0) return;

    setCourse(prev => ({
      ...prev,
      levels: prev.levels.map(l => {
        const u = updates.find(x => x.id === l.id);
        return u ? { ...l, order: u.order } : l;
      }),
    }));
    try {
      await Promise.all(updates.map(u => apiCall('PATCH', `/courses/${courseId}/levels/${u.id}`, { order: u.order }, token)));
    } catch (err) {
      console.error(err);
      const fresh = await apiCall('GET', `/courses/${courseId}`, null, token).catch(() => null);
      if (fresh) setCourse(fresh);
    }
  }

  async function handleAddColumn() {
    const name = newColumnName.trim();
    if (!name) return;
    setColumnBusy(true);
    setColumnError('');
    try {
      const updated = await apiCall('POST', `/courses/${courseId}/columns`, { name, type: newColumnType }, token);
      setCourse(prev => ({ ...prev, ...updated }));
      setNewColumnName('');
      setNewColumnType('text');
    } catch (err) {
      setColumnError(err.message || 'Failed to add column.');
    } finally {
      setColumnBusy(false);
    }
  }

  async function handleDeleteColumn(index) {
    const cols = course.columns ?? [course.sourceColumnName, course.targetColumnName].filter(Boolean);
    const dependents = course.levels.filter(l => l.sourceColumn === index || l.targetColumn === index);
    const wordCount = dependents.reduce((n, l) => n + l.entries.length, 0);

    let msg = `Delete the "${cols[index]}" column? This permanently removes its data and cannot be undone.`;
    if (dependents.length > 0) {
      msg += `\n\n${dependents.length} level${dependents.length === 1 ? '' : 's'} use this column and will also be deleted, along with ${wordCount} word${wordCount === 1 ? '' : 's'} and every learner's progress for them.`;
    }
    if (!window.confirm(msg)) return;

    setColumnBusy(true);
    setColumnError('');
    try {
      const { course: updated } = await apiCall('DELETE', `/courses/${courseId}/columns/${index}`, null, token);
      setCourse(prev => ({ ...prev, ...updated }));
      // Keep the "add level" pickers pointed at columns that still exist.
      setNewLevelSource(0);
      setNewLevelTarget(updated.columns.length > 1 ? 1 : 0);
    } catch (err) {
      setColumnError(err.message || 'Failed to delete column.');
    } finally {
      setColumnBusy(false);
    }
  }

  async function handleDeleteCourse() {
    if (!window.confirm('Delete this course? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/courses/${courseId}`, null, token);
      onDeleted();
    } catch (err) {
      console.error(err);
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <p className="text-amber-600">Loading course…</p>
      </div>
    );
  }

  // Database-backed courses get their columns from the database; custom courses
  // store them on the course itself (older custom courses only have the legacy
  // source/target name pair, so fall back to that).
  const availableColumns = db
    ? db.columns
    : (course.columns ?? [course.sourceColumnName, course.targetColumnName].filter(Boolean));

  // Column types parallel to availableColumns, defaulting any unset entry to 'text'.
  const rawColumnTypes = db ? db.columnTypes : course.columnTypes;
  const availableColumnTypes = availableColumns.map((_, i) =>
    (Array.isArray(rawColumnTypes) && rawColumnTypes[i]) || 'text');
  const hasAudioColumn = availableColumnTypes.includes('audio');
  const orderedCourseLevels = orderLevels(course.levels);

  return (
    <div className="min-h-screen bg-amber-50">
      <header className="bg-white border-b border-amber-100 px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 sticky top-0 z-10">
        <button
          onClick={onBack}
          className="text-amber-600 hover:text-amber-800 font-medium text-sm flex-shrink-0"
        >
          ← Back
        </button>
        <h1 className="text-lg sm:text-xl font-bold text-amber-800 truncate min-w-0">Editing: {course?.title}</h1>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

        {dbMissing && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
            This course's word database could not be loaded — it may have been deleted.
            Its levels won't work for learners, but you can still edit the course details
            or delete the course below.
          </div>
        )}

        {/* Course Settings */}
        <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 sm:p-6 space-y-4">
          <h2 className="font-semibold text-amber-800">Course Settings</h2>

          <div>
            <label className="block text-sm font-medium text-amber-700 mb-1">Course icon</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 flex-shrink-0 rounded-xl bg-amber-100 border border-amber-200 overflow-hidden flex items-center justify-center text-amber-400 text-xs">
                {course.iconUrl ? (
                  <img src={course.iconUrl} alt={`${course.title} icon`} className="w-full h-full object-cover" />
                ) : (
                  'No icon'
                )}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleIconChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingIcon}
                  className="text-sm font-medium text-amber-600 hover:text-amber-800 disabled:text-amber-300 border border-amber-200 hover:border-amber-400 px-4 py-2 rounded-lg transition"
                >
                  {uploadingIcon ? 'Uploading…' : course.iconUrl ? 'Change icon' : 'Upload icon'}
                </button>
                <p className="text-xs text-amber-400 mt-1.5">PNG, JPEG, WebP or GIF · up to 5 MB</p>
              </div>
            </div>
            {iconError && <p className="text-red-500 text-sm mt-2">{iconError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-amber-700 mb-1">Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-amber-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
            />
          </div>

          <div className="flex items-center justify-between py-1">
            <div>
              <p className="text-sm font-medium text-amber-700">Visibility</p>
              <p className="text-xs text-amber-500 mt-0.5">
                {course.isPublished ? 'Published — visible to everyone' : 'Private — only you can see this'}
              </p>
            </div>
            <button
              onClick={handleTogglePublish}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                course.isPublished
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
            >
              {course.isPublished ? '● Published' : '○ Private'}
            </button>
          </div>

          {settingsError && <p className="text-red-500 text-sm">{settingsError}</p>}

          <button
            onClick={handleSaveSettings}
            disabled={savingSettings || !title.trim()}
            className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium px-5 py-2 rounded-lg transition"
          >
            {settingsSaved ? 'Saved ✓' : savingSettings ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* Columns (custom courses only; database-backed courses get their columns
            from the database — even when that database failed to load, the server
            rejects column edits on them, so keep the editor hidden) */}
        {!course.databaseId && (
          <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 sm:p-6 space-y-4">
            <h2 className="font-semibold text-amber-800">Columns</h2>
            <p className="text-xs text-amber-500">
              The fields each word has. Every level picks one column as its question and one as its answer.
              Up to one column can be audio (learners hear it instead of reading). Deleting a column permanently
              removes its data and any levels that use it.
            </p>

            <div className="space-y-2">
              {availableColumns.map((col, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="flex-1 border border-amber-100 bg-amber-50 rounded-lg px-3 py-2 text-sm text-amber-800 break-words">{col}</span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-md ${availableColumnTypes[i] === 'audio' ? 'bg-amber-100 text-amber-700' : 'text-amber-400'}`}>
                    {availableColumnTypes[i] === 'audio' ? '🔊 Audio' : 'Text'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDeleteColumn(i)}
                    disabled={columnBusy || availableColumns.length <= 2}
                    title={availableColumns.length <= 2 ? 'A course needs at least 2 columns' : 'Delete column'}
                    aria-label="Delete column"
                    className="text-amber-300 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition text-lg leading-none px-1"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {availableColumns.length < 10 && (
              <div className="flex items-center gap-2">
                <input
                  value={newColumnName}
                  onChange={e => setNewColumnName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && newColumnName.trim() && handleAddColumn()}
                  placeholder="New column name…"
                  className="flex-1 border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <select
                  value={newColumnType}
                  onChange={e => setNewColumnType(e.target.value)}
                  className="border border-amber-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                >
                  <option value="text">Text</option>
                  <option value="audio" disabled={hasAudioColumn}>Audio</option>
                </select>
                <button
                  type="button"
                  onClick={handleAddColumn}
                  disabled={columnBusy || !newColumnName.trim()}
                  className="text-sm font-medium text-amber-600 hover:text-amber-800 disabled:text-amber-300 border border-amber-200 hover:border-amber-400 px-4 py-2 rounded-lg transition"
                >
                  Add
                </button>
              </div>
            )}

            {columnError && <p className="text-red-500 text-sm">{columnError}</p>}
          </div>
        )}

        {/* Level editors */}
        {course.levels.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold text-amber-800">Levels and lessons</h2>
            <div className="flex gap-4">
              <button type="button" onClick={() => setCollapsedLevels(Object.fromEntries(course.levels.map(level => [level.id, true])))} className="text-sm font-medium text-amber-600 hover:text-amber-800 underline">Collapse all</button>
              <button type="button" onClick={() => setCollapsedLevels({})} className="text-sm font-medium text-amber-600 hover:text-amber-800 underline">Expand all</button>
            </div>
          </div>
        )}
        {course.levels.length === 0 && (
          <p className="text-center text-amber-400 text-sm py-2">No levels yet — add one below.</p>
        )}

        {orderedCourseLevels.map((level, i) => {
          const collapseProps = {
            collapsed: !!collapsedLevels[level.id],
            onToggle: () => setCollapsedLevels(prev => ({ ...prev, [level.id]: !prev[level.id] })),
          };
          const controls = (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button type="button" onClick={() => handleMoveLevel(level.id, -1)} disabled={i === 0} title="Move up" aria-label="Move up" className="text-amber-400 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed px-1">▲</button>
              <button type="button" onClick={() => handleMoveLevel(level.id, 1)} disabled={i === orderedCourseLevels.length - 1} title="Move down" aria-label="Move down" className="text-amber-400 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed px-1">▼</button>
              <button type="button" onClick={() => handleDeleteLevel(level.id, level.title)} title="Delete level" aria-label="Delete level" className="text-amber-300 hover:text-red-500 text-xl leading-none px-1.5">×</button>
            </div>
          );
          return isLessonLevel(level) ? (
            <LessonLevelEditor
              key={level.id}
              {...collapseProps}
              level={level}
              courseId={courseId}
              token={token}
              columns={availableColumns}
              controls={controls}
              onSaved={handleLevelSaved}
              precedingLevelCount={i}
            />
          ) : (
            <LevelEditor
              key={level.id}
              {...collapseProps}
              level={level}
              courseId={courseId}
              db={db}
              columns={availableColumns}
              columnTypes={availableColumnTypes}
              token={token}
              onEntriesChange={handleEntriesChange}
              controls={controls}
            />
          );
        })}

        {/* Add Level */}
        <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 sm:p-6 space-y-4">
          <h2 className="font-semibold text-amber-800">Add a Level</h2>

          <div>
            <label className="block text-sm font-medium text-amber-700 mb-1">Level title</label>
            <input
              value={newLevelTitle}
              onChange={e => setNewLevelTitle(e.target.value)}
              placeholder="e.g. Greetings, Numbers, Food…"
              className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          {availableColumns.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">Question column</label>
                <select
                  value={newLevelSource}
                  onChange={e => setNewLevelSource(Number(e.target.value))}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                >
                  {availableColumns.map((col, i) => (
                    <option key={i} value={i}>{col}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">Answer column</label>
                <select
                  value={newLevelTarget}
                  onChange={e => setNewLevelTarget(Number(e.target.value))}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                >
                  {availableColumns.map((col, i) => (
                    <option key={i} value={i}>{col}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {levelError && <p className="text-red-500 text-sm">{levelError}</p>}

          <button
            onClick={handleAddLevel}
            disabled={addingLevel || !newLevelTitle.trim()}
            className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium px-5 py-2 rounded-lg transition"
          >
            {addingLevel ? 'Adding…' : '+ Add Level'}
          </button>
        </div>

        {/* Add a Lesson — tutor-taught grammar/reading/writing. Official courses only
            (the server enforces this too); a course is flagged official out-of-band. */}
        {course.isOfficial ? (
          <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 sm:p-6 space-y-4">
            <h2 className="font-semibold text-amber-800">Add a lesson or grammar drill</h2>
            <p className="text-xs text-amber-500">
              {newLessonType === 'grammar-drill'
                ? 'Generate translation practice for a grammar point taught earlier. After adding the drill, set its focus, languages, phrase count, and optional instructions for the AI.'
                : newLessonType === 'reading'
                  ? 'Add a passage and comprehension questions, with reference answers and evidence from the text.'
                  : newLessonType === 'writing'
                    ? 'Set writing tasks, review criteria, and optional sample answers for learners to practise their writing.'
                    : 'Teach a grammar concept with an explanation, examples, and practice exercises.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">Type</label>
                <select
                  value={newLessonType}
                  onChange={e => setNewLessonType(e.target.value)}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  <option value="grammar">Grammar</option>
                  <option value="reading">Reading</option>
                  <option value="writing">Writing</option>
                  <option value="grammar-drill">Grammar drill</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">{newLessonType === 'grammar-drill' ? 'Drill title' : 'Lesson title'}</label>
                <input
                  value={newLessonTitle}
                  onChange={e => setNewLessonTitle(e.target.value)}
                  placeholder={newLessonType === 'grammar-drill' ? 'e.g. Practise present-tense conjugations' : 'e.g. Subject pronouns + être'}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>
            {lessonError && <p className="text-red-500 text-sm">{lessonError}</p>}
            {newLessonType === 'grammar' && <LessonPlannerChat
              courseId={courseId} token={token} precedingLevelCount={orderedCourseLevels.length}
              draft={{ title: newLessonTitle, sourceColumn: 0, targetColumn: availableColumns.length > 1 ? 1 : 0 }}
            />}
            <button
              onClick={handleAddLesson}
              disabled={addingLesson || !newLessonTitle.trim()}
              className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-medium px-5 py-2 rounded-lg transition"
            >
              {addingLesson ? 'Adding…' : newLessonType === 'grammar-drill' ? '+ Add Drill' : '+ Add Lesson'}
            </button>
          </div>
        ) : (
          <p className="text-center text-xs text-amber-400">
            Lessons and grammar drills are available on official courses.
          </p>
        )}

        {/* Danger Zone */}
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-5 sm:p-6 space-y-3">
          <h2 className="font-semibold text-red-700">Danger Zone</h2>
          <p className="text-sm text-amber-600">
            Deleting this course permanently removes it and all its levels. This cannot be undone.
          </p>
          <button
            onClick={handleDeleteCourse}
            disabled={deleting}
            className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white text-sm font-medium px-5 py-2 rounded-lg transition"
          >
            {deleting ? 'Deleting…' : 'Delete Course'}
          </button>
        </div>

      </div>
    </div>
  );
}
